import { Button, Input, InputRef, Popover, Select } from "antd";
import { ColumnsType } from "antd/es/table";
import { useRef } from "react";
import AddIcon from "../../../../assets/icons/AddIcon";
import EditIcon from "../../../../assets/icons/EditIcon";
import SearchIcon from "../../../../assets/icons/SearchIcon";
import SelectDownIcon from "../../../../assets/icons/SelectDownIcon";
import UsersIcon from "../../../../assets/icons/UsersIcon";
import IMG from "../../../../assets/images/avatar.svg";
import AddNewApprovalModal, {
  approverModuleList,
} from "../../../../components/Settings/Approval/AddNewApprovalModal";
import CustomTable from "../../../../components/ui/CustomTable";
import { useGetDepartmentsQuery } from "../../../../features/department/departmentApi";
import { useGetApprovalSettingQuery } from "../../../../features/leaveApproval/leaveApprovalApi";
import useError from "../../../../hooks/useError";
import useModal from "../../../../hooks/useModal";
import useTable from "../../../../hooks/useTable";
import SidebarLayout from "../../../../layouts/SidebarLayout";
import {
  GetApprovalSettingParams,
  GetApprovalSettingResponseType,
} from "../../../../types";
import { countParPageSerialNumber } from "../../../../utils/number";
import {
  addKeyInArray,
  selectArrayOption,
  selectSearchOption,
  selectStringOption,
  underscoreToCapitalizeString,
} from "../../../../utils/utils";

export type ApprovalData =
  GetApprovalSettingResponseType["data"]["approval_settings"][0];

export default function Approval() {
  const searchRef = useRef<InputRef>(null);
  const {
    page,
    limit,
    handlePagination,
    handleLimit,
    filter,
    keyword,
    handleFilter,
    handleSearch,
    handleSearchEnter,
  } = useTable<GetApprovalSettingParams>();
  const { open, handleClose, handleOpen } = useModal<ApprovalData>();
  const {
    isError: isDepartmentError,
    error: departmentError,
    data: departmentData,
  } = useGetDepartmentsQuery({
    params: { skip: 0, limit: 0, is_published: 1 },
  });
  const { isLoading, isError, error, data } = useGetApprovalSettingQuery({
    params: {
      search: keyword,
      ...filter,
    },
  });
  useError(isDepartmentError, departmentError, "Get department data fail");
  useError(isError, error, "Get approval data fail");

  const columns: ColumnsType<ApprovalData> = [
    {
      title: "#SL",
      align: "center",
      width: 60,
      render: (_, __, index) => countParPageSerialNumber(page, limit, index),
    },
    {
      title: "Module",
      dataIndex: "modules_name",
      render: (val) => underscoreToCapitalizeString(val),
    },
    {
      title: "Type",
      dataIndex: "target_type",
      render: (val) => underscoreToCapitalizeString(val),
    },
    {
      title: "Employee",
      render: (val) => {
        const empList = val?.target_type_details;
        return val?.target_type === "employee" ? (
          <div>
            <h6>{empList?.name}</h6>
            <p className="font-bold !text-xs text-display-body -mt-0.5">
              ID - {empList?.employee_id || "n/a"}
            </p>
          </div>
        ) : (
          "All"
        );
      },
    },
    {
      title: "Department",
      render: (val) =>
        val?.target_type === "global"
          ? "All"
          : val?.target_type_details?.department_name || "B2B",
    },
    {
      title: "Approvers",
      dataIndex: "approvers",
      render: (val) => {
        const content = (
          <div className="flex flex-col gap-2">
            {val?.map((item: ApprovalData["approvers"][0]) => (
              <div className="flex items-center gap-2" key={item?.id}>
                <img
                  className="w-8 h-8 rounded-full"
                  src={item?.approver_details?.profile_image || IMG}
                  alt=""
                />
                <div className="flex-1 h-full flex flex-col gap-[2px] text-sm font-medium text-display-body">
                  {item?.type === "lm" ? (
                    <h6 className="text-body text-sm font-medium">
                      Line Manager (LM)
                    </h6>
                  ) : item?.approver_details?.name ? (
                    <h6 className="text-body text-sm font-medium">
                      {item?.approver_details?.name}
                    </h6>
                  ) : null}
                  {(item?.approver_details?.employee_id ||
                    val?.department_name) && (
                    <p className="text-extra-gray !text-xs">
                      ID: {item?.approver_details?.employee_id || "n/a"}{" "}
                      {item?.approver_details?.department_name ? (
                        <span>, {item?.approver_details?.department_name}</span>
                      ) : null}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        );

        return (
          <div className="flex items-center gap-1">
            {val?.length}
            <Popover content={content} trigger="click">
              <UsersIcon className="text-accent cursor-pointer" size={18} />
            </Popover>
          </div>
        );
      },
    },
    { title: "Others", dataIndex: "note", render: (val) => val || "n/a" },
    {
      title: "Action",
      render: (val) => (
        <Button
          className="action-button"
          size="small"
          icon={<EditIcon />}
          onClick={() => handleOpen({ type: "UPDATE", data: val })}
        />
      ),
    },
  ];

  return (
    <SidebarLayout title="Approval">
      <CustomTable
        loading={isLoading}
        limit={limit}
        page={page}
        columns={columns}
        total={data?.data?.total_count}
        dataSource={addKeyInArray(data?.data?.approval_settings)}
        handleLimit={handleLimit}
        handlePagination={handlePagination}
        filterSection={
          <>
            <Input
              key={filter?.type}
              suffix={<SearchIcon />}
              placeholder="Search"
              onChange={handleSearch}
              disabled={filter?.type !== "employee"}
              ref={searchRef}
            />
            <Select
              allowClear
              showSearch
              placeholder="Department"
              filterOption={selectSearchOption}
              options={selectArrayOption(
                departmentData?.data?.departments || [],
                "name",
                "id"
              )}
              className="min-w-[130px]"
              suffixIcon={<SelectDownIcon />}
              rootClassName="icon-gray"
              onChange={(val) => {
                handleFilter("department", val);
              }}
              value={filter?.department || undefined}
              disabled={filter?.type !== "department"}
            />
            <Select
              allowClear
              showSearch
              placeholder="Type"
              filterOption={true}
              options={selectStringOption(typeList, "string", true)}
              className="min-w-[120px]"
              suffixIcon={<SelectDownIcon />}
              rootClassName="icon-gray"
              onChange={(val) => {
                handleFilter("type", val);
                handleFilter("department", undefined);
                handleSearchEnter(undefined);
              }}
            />
            <Select
              allowClear
              showSearch
              placeholder="Module"
              filterOption={true}
              options={selectStringOption(approverModuleList, "string", true)}
              className="min-w-[120px]"
              suffixIcon={<SelectDownIcon />}
              rootClassName="icon-gray"
              onChange={(val) => {
                handleFilter("modules", val);
              }}
            />
            <Button
              className="flex items-center gap-2"
              type="primary"
              onClick={() =>
                handleOpen({
                  type: "ADD",
                })
              }
            >
              <AddIcon />
              <span>Add New Approval</span>
            </Button>
          </>
        }
      />

      {(open.type === "ADD" || open?.type === "UPDATE") && (
        <AddNewApprovalModal
          open={open.open}
          data={open?.data}
          type={open?.type}
          handleClose={handleClose}
        />
      )}
    </SidebarLayout>
  );
}

const typeList = ["global", "department", "employee"];
